import { Canvas, FabricObject } from "fabric";
import { useEffect, useRef } from "react";
import { useOnMount } from "../../../../../../hooks/useOnMount.ts";

FabricObject.prototype.set({
  cornerSize: 6,
});

export const FabricCanvas = ({
  initWidth,
  initHeight,
  initBackgroundColor,
  isCanvasHidden,
  isDrawingMode = false,
  onCanvasReady,
}: {
  initWidth: number;
  initHeight: number;
  initBackgroundColor?: string;
  isCanvasHidden?: boolean;
  isDrawingMode?: boolean;
  onCanvasReady: (canvas: Canvas) => void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<Canvas | null>(null);
  const prevDimensionsRef = useRef<{ width: number; height: number }>({
    width: initWidth,
    height: initHeight,
  });

  useOnMount(() => {
    if (!canvasRef.current) return;

    const fabricCanvas = new Canvas(canvasRef.current, {
      height: initHeight,
      width: initWidth,
      backgroundColor: initBackgroundColor,
      isDrawingMode,
      preserveObjectStacking: true,
    });
    fabricRef.current = fabricCanvas;

    return () => void fabricCanvas.dispose();
  });

  useEffect(() => {
    if (!fabricRef.current) return;
    onCanvasReady(fabricRef.current);
  }, [onCanvasReady]);

  useEffect(() => {
    if (!fabricRef.current) return;

    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    // Update the canvas element dimensions and the fabric.js canvas dimensions
    canvasEl.width = initWidth;
    canvasEl.height = initHeight;

    fabricRef.current.setWidth(initWidth);
    fabricRef.current.setHeight(initHeight);

    // Update viewport transform to maintain zoom level
    const zoom = fabricRef.current.getZoom();
    fabricRef.current.setZoom(zoom);

    fabricRef.current.renderAll();
    prevDimensionsRef.current = { width: initWidth, height: initHeight };
  }, [initWidth, initHeight]);

  return (
    <canvas
      ref={canvasRef}
      style={{ display: isCanvasHidden ? "none" : "block" }}
    />
  );
};
